import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { getAccountCocInSchedule } from '@app/reducers/account/account-service.selectors';
import { getEmployeeEntities } from '@app/reducers/orm/employee/employee.selectors';
import { RosterApi } from '@app/reducers/orm/schedule/roster.api';
import { BestFitSource, ScheduleModel } from '@app/reducers/orm/schedule/schedule.model';
import { getEnhancedScheduleEntities, ScheduleService } from '@app/reducers/orm/schedule/schedule.service';
import { BestFitCandidateRow } from '@app/shared/employability/best-fit-employee-list/best-fit-candidate-row.model';
import { BestFitEmployeeListComponent } from '@app/shared/employability/best-fit-employee-list/best-fit-employee-list.component';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { mgcIconAlertRegular } from '@sb/mgc-icons/mgcIcon-alert_regular.icon';
import { mgcIconCheckRegular } from '@sb/mgc-icons/mgcIcon-check_regular.icon';
import { mgcIconCloseCircleRegular } from '@sb/mgc-icons/mgcIcon-close_circle_regular.icon';
import { mgcIconEmptyBoxRegular } from '@sb/mgc-icons/mgcIcon-empty_box_regular.icon';
import { mgcIconPhoneRegular } from '@sb/mgc-icons/mgcIcon-phone_regular.icon';
import { AnchorButtonComponent, ButtonComponent } from '@sb/ui/button';
import { CardComponent } from '@sb/ui/card';
import { CheckboxComponent } from '@sb/ui/checkbox';
import { DIALOG_DATA, DialogRef, SbDialogModule } from '@sb/ui/dialog';
import { EmptyStateComponent, LoadingStateComponent } from '@sb/ui/feedback';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import { IconBadgeComponent } from '@sb/ui/icon-badge';
import { LaddaDirective } from '@sb/ui/ladda';
import { take } from 'rxjs';

import { toReassignPayload } from './reassign-payload.helper';
import { toShiftPlacement } from './shift-placement.helper';
import { FindCoverShiftRowComponent } from './shift-row/find-cover-shift-row.component';

/**
 * Opened programmatically via FindCoverService — the planner reaches it from a
 * shift in the schedule or from the prompt after an absence saves, neither of
 * which is a route.
 *
 * More than one occurrence turns the dialog into a stepper over the day's shifts;
 * a single one behaves exactly as it always has.
 */
export interface FindCoverDialogData {
  occurrenceIds: string[];
}

/** Employee assigned to each shift the run covered, keyed by occurrence id. */
export type FindCoverAssignments = Record<string, string>;

/** Names the flow to the backend, on both the recommendation request and the write. */
const BEST_FIT_SOURCE: BestFitSource = 'absence';

@Component({
  templateUrl: './schedule-find-cover.component.html',
  imports: [
    SbDialogModule,
    TranslatePipe,
    LocaleDatePipe,
    ButtonComponent,
    CheckboxComponent,
    FormsModule,
    LoadingStateComponent,
    EmptyStateComponent,
    BestFitEmployeeListComponent,
    LaddaDirective,
    IconComponent,
    AnchorButtonComponent,
    CardComponent,
    IconBadgeComponent,
    FindCoverShiftRowComponent,
  ],
})
export class ScheduleFindCoverComponent {
  #data = inject<FindCoverDialogData>(DIALOG_DATA);
  #dialogRef = inject<DialogRef<FindCoverAssignments>>(DialogRef);
  #store = inject<Store<AppState>>(Store);
  #rosterApi = inject(RosterApi);
  #scheduleService = inject(ScheduleService);
  #destroyRef = inject(DestroyRef);

  selectedEmployeeIds = signal<string[]>([]);
  notifyEmployee = signal(true);
  isSaving = signal(false);

  /** Position in the run; always 0 for the single-shift flow. */
  #currentIndex = signal(0);

  #assignedEmployeeIds = signal<FindCoverAssignments>({});

  totalSteps = this.#data.occurrenceIds.length;

  isStepper = this.totalSteps > 1;

  stepNumber = computed(() => this.#currentIndex() + 1);

  isLastStep = computed(() => this.stepNumber() === this.totalSteps);

  #currentOccurrenceId = computed(() => this.#data.occurrenceIds[this.#currentIndex()]);

  #employeeEntities = this.#store.selectSignal(getEmployeeEntities);

  #scheduleEntities = this.#store.selectSignal(getEnhancedScheduleEntities);

  showCoc = this.#store.selectSignal(getAccountCocInSchedule);

  shift = computed<ScheduleModel | undefined>(() => this.#scheduleEntities()[this.#currentOccurrenceId()]);

  shiftPlacement = computed(() => toShiftPlacement(this.shift()));

  #bestFit = rxResource({
    params: () => this.#currentOccurrenceId(),
    stream: ({ params: occurrenceId }) => this.#rosterApi.getBestFit(occurrenceId, BEST_FIT_SOURCE),
  });

  isLoading = this.#bestFit.isLoading;

  #error = computed(() => (this.#bestFit.status() === 'error' ? this.#bestFit.error() : undefined));

  #errorStatus = computed(() => {
    const error = this.#error();

    return error instanceof HttpErrorResponse ? error.status : undefined;
  });

  isIncomplete = computed(() => this.#errorStatus() === 422);

  isUnavailable = computed(() => this.#errorStatus() === 503);

  hasError = computed(() => !!this.#error() && !this.isUnavailable() && !this.isIncomplete());

  rows = computed<BestFitCandidateRow[]>(() => {
    const employees = this.#employeeEntities();

    // A candidate whose employee is not in the store cannot be rendered or
    // deselected, so it is dropped rather than shown half-populated.
    return (this.#bestFit.value() ?? [])
      .filter((candidate) => employees[candidate.employeeId])
      .map((candidate) => ({ candidate, employee: employees[candidate.employeeId] }));
  });

  hasSelection = computed(() => this.selectedEmployeeIds().length > 0);

  /** Holds the candidate the contact view is open for; null shows the candidate list instead. */
  contactedEmployeeId = signal<string | null>(null);

  contactedEmployee = computed(() => {
    const employeeId = this.contactedEmployeeId();

    return employeeId ? this.#employeeEntities()[employeeId] : undefined;
  });

  /** Mobile first, as every other contact surface does — see absence.service.ts. */
  contactPhoneNumber = computed(() => {
    const employee = this.contactedEmployee();

    return employee?.mobile_nr?.trim() || employee?.phone_nr?.trim() || undefined;
  });

  contactPhoneHref = computed(() => {
    const phoneNumber = this.contactPhoneNumber();

    // A tel: URI cannot carry the spaces the number is displayed with.
    return phoneNumber ? `tel:${phoneNumber.replace(/\s/g, '')}` : undefined;
  });

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconCloseCircleRegular,
      mgcIconEmptyBoxRegular,
      mgcIconAlertRegular,
      mgcIconCheckRegular,
      mgcIconPhoneRegular,
    ]);
  }

  openContact(): void {
    this.contactedEmployeeId.set(this.selectedEmployeeIds()[0] ?? null);
  }

  closeContact(): void {
    this.contactedEmployeeId.set(null);
  }

  skip(): void {
    this.#advance();
  }

  reassign(): void {
    const [employeeId] = this.selectedEmployeeIds();
    const shift = this.shift();
    const occurrenceId = this.#currentOccurrenceId();

    if (!employeeId || !shift || this.isSaving()) {
      return;
    }

    this.#setSaving(true);

    this.#scheduleService
      .update(
        occurrenceId,
        toReassignPayload(shift, employeeId),
        // Never 'sequence': reassigning must not touch the other occurrences of a
        // repeating shift. Non-recurring rosters take 'original', as every other
        // update call site does.
        shift.recurring ? 'occurrence' : 'original',
        this.notifyEmployee(),
        BEST_FIT_SOURCE,
      )
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.#assignedEmployeeIds.update((assigned) => ({ ...assigned, [occurrenceId]: employeeId }));
          this.#setSaving(false);
          this.#advance();
        },
        error: () => this.#setSaving(false),
      });
  }

  /**
   * Sealing the dialog for the duration of the write is what the footer's disabled Skip
   * and primary already do. Escape has to be held back too: destroying the component
   * mid-flight lets takeUntilDestroyed abort the request after ScheduleService has
   * dispatched its optimistic update, and nothing is left to confirm or undo it.
   */
  #setSaving(isSaving: boolean): void {
    this.isSaving.set(isSaving);
    this.#dialogRef.disableClose = isSaving;
  }

  /**
   * Closing with the assignments is what tells FindCoverService the run reached its
   * end; dismissing the dialog yields undefined instead, which is why no summary
   * follows an abandoned run.
   */
  #advance(): void {
    if (this.isLastStep()) {
      this.#dialogRef.close(this.#assignedEmployeeIds());

      return;
    }

    this.#currentIndex.update((index) => index + 1);
    // Selection and the contact view belong to the shift just left behind. The notify
    // choice deliberately survives — it is one decision for the whole run.
    this.selectedEmployeeIds.set([]);
    this.contactedEmployeeId.set(null);
  }
}
